import { Injectable, signal, TemplateRef } from "@angular/core";

export interface DialogOptions {
  title: string;
  /** main body text */
  message?: string;
  /** rich body markup (links, emphasis) — rendered via [innerHTML], Angular-sanitized */
  messageHtml?: string;
  /** optional second, muted paragraph (e.g. consequences of the action) */
  note?: string;
  /** escape hatch for complex bodies (tables, images) — rendered below message */
  template?: TemplateRef<any>;
  /** label for the confirm button — defaults to "Confirm" ("Okay" for alerts) */
  confirmLabel?: string;
  /** label for the cancel button — defaults to "Cancel" */
  cancelLabel?: string;
  /** optional live predicate used to disable the confirm action */
  confirmDisabled?: () => boolean;
  /** "danger" renders a red confirm button for destructive actions */
  variant?: "primary" | "danger";
  /** wider cards for rich bodies — default 420px, wide = 560px, xwide = 960px */
  size?: "default" | "wide" | "xwide";
}

export interface ActiveDialog extends DialogOptions {
  mode: "confirm" | "alert";
}

/**
 * App-wide confirmation/alert dialogs.
 *
 * One <app-confirm-dialog> instance lives in AppComponent; call
 * `dialog.confirm({...}).then(confirmed => ...)` or `dialog.alert({...})`
 * from anywhere — no per-component modal markup or ViewChild plumbing.
 */
@Injectable({ providedIn: "root" })
export class DialogService {
  private state = signal<ActiveDialog | null>(null);
  /** the dialog currently on screen (null when closed) — read by the component */
  readonly active = this.state.asReadonly();

  private resolver: ((confirmed: boolean) => void) | null = null;

  /** Cancel / Confirm pair; resolves true only on confirm */
  confirm(options: DialogOptions): Promise<boolean> {
    return this.open({ ...options, mode: "confirm" });
  }

  /** single Okay button; resolves when dismissed */
  alert(options: DialogOptions): Promise<boolean> {
    return this.open({ ...options, mode: "alert" });
  }

  private open(dialog: ActiveDialog): Promise<boolean> {
    // opening a new dialog cancels any dialog already on screen
    if (this.resolver) {
      this.resolver(false);
    }
    return new Promise<boolean>((resolve) => {
      this.resolver = resolve;
      this.state.set(dialog);
    });
  }

  /** called by the dialog component when a button is pressed */
  close(confirmed: boolean) {
    this.state.set(null);
    const resolve = this.resolver;
    this.resolver = null;
    resolve?.(confirmed);
  }
}
